/**
 * PdfPreviewPane
 * ==============
 * A live HTML representation of what the actual PDF will look like.
 * Updates instantly as the contractor edits the estimate — no roundtrip
 * to the backend PDF generator. The "Download PDF" button still hits
 * /api/quotes/{id}/pdf for the real ReportLab-rendered file.
 *
 * Important: this preview deliberately mirrors the structure of
 * services/pdf.py so what a contractor sees IS what their customer
 * sees. If you change one, change the other.
 *
 * Styling: monochrome navy + amber, generous spacing, serif body
 * font for the "feels like a real document" cue. Width of the
 * outer card matches a letter-size aspect ratio when constrained.
 */
import React, { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';

const $ = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SCOPE_LABELS = {
    tearoff:       'Tear-off & disposal of existing roof',
    underlayment:  'Synthetic underlayment installation',
    ice_water:     'Ice & water shield at eaves + valleys',
    drip_edge:     'New drip edge along eaves + rakes',
    starter_strip: 'Starter strip course',
    shingles:      'New asphalt shingles installed per spec',
    ridge_cap:     'Hip & ridge cap installation',
    flashing:      'New pipe boots, vent flashing, step/counter flashing',
    ridge_vent:    'Continuous ridge vent',
    decking:       'Decking replacement as needed',
    permit:        'Building permit',
    haul_off:      'Haul-off & dump fee',
    cleanup:       'Magnetic nail sweep + property cleanup',
};
const SCOPE_DEFAULT_CHECKED = ['tearoff', 'underlayment', 'ice_water', 'drip_edge', 'starter_strip', 'shingles', 'ridge_cap', 'flashing', 'haul_off', 'cleanup'];


export function PdfPreviewPane({ quote, subtotal, tax, total, rangeLow, rangeHigh }) {
    const company = quote.company_info || {};
    const scope = quote.scope_of_work || {};
    const scopeItems = scope.items || {};
    const materials = quote.materials || [];
    const warranties = quote.warranties || {};
    const payment = quote.payment_terms || {};
    const timeline = quote.timeline || {};
    const customSections = quote.custom_sections || [];

    // Resolve which scope items are "on" — match the EstimateBuilder logic
    // so the preview shows the same check-state as the form.
    const activeScope = useMemo(() => {
        return Object.entries(SCOPE_LABELS)
            .filter(([key]) => {
                const explicit = scopeItems[key];
                if (explicit !== undefined) return !!explicit;
                return SCOPE_DEFAULT_CHECKED.includes(key);
            })
            .map(([_, label]) => label);
    }, [scopeItems]);

    return (
        <div
            className="text-slate-900 bg-white p-6 sm:p-8 text-[13px] leading-relaxed"
            style={{ fontFamily: '"Source Serif Pro", Georgia, serif' }}
            data-testid="pdf-preview-pane"
        >
            {/* Document header */}
            <div className="flex items-start justify-between gap-3 pb-3 border-b-2 border-amber-500">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-bold" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                        Roofing Estimate
                    </div>
                    <h2 className="font-display text-xl text-slate-900 mt-1" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
                        {company.legal_name || 'Your Company Name'}
                    </h2>
                    <div className="text-[11px] text-slate-600 mt-0.5">
                        {company.address || 'Your business address'}
                        {company.phone ? ` · ${company.phone}` : ''}
                        {company.email ? ` · ${company.email}` : ''}
                    </div>
                    {(company.license_number || company.license_state) && (
                        <div className="text-[10px] text-slate-500 mt-0.5">
                            License: {company.license_number || '—'}{company.license_state ? ` (${company.license_state})` : ''}
                        </div>
                    )}
                </div>
                <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500" style={{ fontFamily: 'Inter, sans-serif' }}>Quote #</div>
                    <div className="font-mono text-sm">{quote.quote_number || '—'}</div>
                    <div className="text-[10px] text-slate-500 mt-1">
                        {new Date(quote.created_at || Date.now()).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </div>
                </div>
            </div>

            {/* Prepared for */}
            <PreviewSection num="1" title="Prepared For">
                <div>{quote.homeowner_name || <em className="text-slate-400">Homeowner name</em>}</div>
                <div className="text-[12px] text-slate-700">{quote.property_address || <em className="text-slate-400">Property address</em>}</div>
            </PreviewSection>

            {/* Property summary */}
            <PreviewSection num="2" title="Property Summary">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <KV k="Stories" v={`${quote.stories || 1}`} />
                    <KV k="Roof sqft" v={quote.roof?.measurements?.roof_sqft ? Math.round(quote.roof.measurements.roof_sqft).toLocaleString() : '—'} />
                    <KV k="Material" v={(quote.roof?.measurements?.material_guess || 'TBD').replace(/_/g, ' ')} />
                    <KV k="Pitch" v={quote.roof?.measurements?.pitch || 'TBD'} />
                </div>
            </PreviewSection>

            {/* Scope of work */}
            <PreviewSection num="3" title="Scope of Work">
                {activeScope.length === 0 ? (
                    <em className="text-slate-400">No scope items selected yet.</em>
                ) : (
                    <ul className="list-disc pl-5 space-y-0.5 text-[12px]">
                        {activeScope.map((label, i) => <li key={i}>{label}</li>)}
                    </ul>
                )}
                {scope.notes && (
                    <p className="text-[11px] text-slate-600 mt-2 italic">{scope.notes}</p>
                )}
            </PreviewSection>

            {/* Materials */}
            {materials.length > 0 && (
                <PreviewSection num="4" title="Materials">
                    <table className="w-full text-[11px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                        <thead>
                            <tr className="border-b border-slate-200 text-slate-500 uppercase tracking-wider text-[9px]">
                                <th className="text-left py-1 font-semibold">Category</th>
                                <th className="text-left py-1 font-semibold">Spec</th>
                                <th className="text-right py-1 font-semibold">Quantity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {materials.map((m, i) => (
                                <tr key={i} className="border-b border-slate-50">
                                    <td className="py-1 text-slate-900">{m.category || '—'}</td>
                                    <td className="py-1 text-slate-700">{m.spec || '—'}</td>
                                    <td className="py-1 text-right text-slate-700 tabular-nums">{m.quantity || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </PreviewSection>
            )}

            {/* Investment summary — homeowner range, then total */}
            <PreviewSection num="5" title="Your Investment">
                <div className="rounded-lg border-2 border-amber-300 bg-amber-50/50 p-3 mb-2">
                    <div className="text-[9px] uppercase tracking-wider text-amber-800 font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Estimated total
                    </div>
                    <div className="font-display text-xl text-slate-900 mt-0.5 tabular-nums" style={{ fontFamily: '"Playfair Display", serif' }}>
                        {$ (rangeLow)} <span className="text-slate-400 mx-1">–</span> {$ (rangeHigh)}
                    </div>
                </div>
                <div className="space-y-0.5 text-[12px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <KV k="Subtotal" v={$ (subtotal)} />
                    {tax > 0 && <KV k="Tax" v={$ (tax)} />}
                    <KV k="Total" v={$ (total)} bold />
                </div>
            </PreviewSection>

            {/* Payment terms */}
            <PreviewSection num="6" title="Payment Terms">
                <div className="text-[11px] space-y-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {payment.deposit && <div><b>Deposit:</b> {payment.deposit}</div>}
                    {payment.progress && <div><b>Progress payment:</b> {payment.progress}</div>}
                    {payment.final && <div><b>Final payment:</b> {payment.final}</div>}
                    {payment.methods && <div><b>Accepted methods:</b> {payment.methods}</div>}
                    {!payment.deposit && !payment.final && (
                        <em className="text-slate-400">No payment terms set yet.</em>
                    )}
                </div>
            </PreviewSection>

            {/* Warranties */}
            <PreviewSection num="7" title="Warranties">
                <div className="text-[11px] space-y-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <div><b>Workmanship:</b> {warranties.workmanship || '10-year workmanship warranty against installation defects'}</div>
                    <div><b>Manufacturer:</b> {warranties.manufacturer || 'Per shingle manufacturer terms — Limited Lifetime Material Warranty'}</div>
                </div>
            </PreviewSection>

            {/* Timeline */}
            {(timeline.start_date || timeline.duration) && (
                <PreviewSection num="8" title="Timeline">
                    <div className="text-[11px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {timeline.start_date && <div><b>Start:</b> {timeline.start_date}</div>}
                        {timeline.duration && <div><b>Duration:</b> {timeline.duration}</div>}
                    </div>
                </PreviewSection>
            )}

            {/* Custom sections — numbered 9+ to match the PDF */}
            {customSections.map((sec, i) => {
                const title = (sec.title || '').trim();
                const body = (sec.body || '').trim();
                if (!title && !body) return null;
                return (
                    <PreviewSection key={sec.id || i} num={String(9 + i)} title={title || 'Additional Notes'}>
                        {body && (
                            <div className="text-[12px] text-slate-700 whitespace-pre-wrap" style={{ fontFamily: 'Inter, sans-serif' }}>
                                {body}
                            </div>
                        )}
                    </PreviewSection>
                );
            })}

            {/* Signature line */}
            <div className="mt-6 pt-4 border-t border-slate-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-500" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Homeowner Acceptance
                </div>
                <div className="mt-2 text-[12px] text-slate-400 italic">Signature: ___________________________  Date: _______________</div>
            </div>

            <div className="mt-4 pt-3 text-[9px] text-center text-slate-400" style={{ fontFamily: 'Inter, sans-serif' }}>
                This quote is valid 30 days from issue date. Powered by QuotesFlow AI Roofing.
            </div>
        </div>
    );
}


function PreviewSection({ num, title, children }) {
    return (
        <div className="mt-4">
            <h3 className="font-display text-[14px] text-slate-900 mb-1.5" style={{ fontFamily: '"Playfair Display", serif' }}>
                <span className="text-amber-700">{num}.</span> {title}
            </h3>
            <div className="pl-0">{children}</div>
        </div>
    );
}


function KV({ k, v, bold }) {
    return (
        <div className={`flex items-center justify-between ${bold ? 'font-semibold text-slate-900 border-t border-slate-200 pt-1 mt-1' : 'text-slate-700'}`}>
            <span>{k}</span>
            <span className="tabular-nums">{v}</span>
        </div>
    );
}
